'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { type DisplayDateAnchor } from '@/i18n/dates'

import type { SearchCarrierFilter } from './use-search-submit'
import { useWidgetState, type WidgetState, type WidgetStateTuple } from './use-widget-state'

/** `null` is "no surface-owned selection here" — a widget then owns its own. */
const SearchSelectionContext = createContext<WidgetStateTuple | null>(null)

/** The carrier the surface narrows its searches to, if any. */
const SearchCarrierContext = createContext<SearchCarrierFilter | undefined>(undefined)

interface SearchSelectionProviderProps {
    /** The surface's initial values, supplied once for every presentation beneath. */
    initial?: Partial<WidgetState>
    /** Required, as for any owner of a selection — see `useWidgetState`. */
    dateAnchor: DisplayDateAnchor
    /** The carrier code every search started beneath is narrowed to. */
    carrier?: string
    children: ReactNode
}

/**
 * Holds **one** caller-owned selection for every widget presentation rendered beneath
 * it: a page mounting more than one presentation puts them in
 * lockstep without either becoming the other's parent, and one owner means one reader
 * and one writer of the stored selection.
 *
 * Feature-scoped: mounted by the pages that need it, not by a layout
 * (`components.md#effects-vs-providers`).
 */
export function SearchSelectionProvider({
    initial,
    dateAnchor,
    carrier,
    children,
}: SearchSelectionProviderProps) {
    const selection = useWidgetState(initial, dateAnchor)
    const filter = useMemo(() => (carrier ? { carrier } : undefined), [carrier])

    return (
        <SearchSelectionContext.Provider value={selection}>
            <SearchCarrierContext.Provider value={filter}>{children}</SearchCarrierContext.Provider>
        </SearchSelectionContext.Provider>
    )
}

/** The carrier the enclosing surface narrows searches to, or `undefined`. */
export function useSearchCarrier(): SearchCarrierFilter | undefined {
    return useContext(SearchCarrierContext)
}

/** The surface-owned selection, or `null` when no provider encloses the caller. */
export function useSearchSelection(): WidgetStateTuple | null {
    return useContext(SearchSelectionContext)
}
